import { queryOptions, useQuery } from '@tanstack/react-query'
import { fetchEmployeeById } from '../api/user.service.ts'
import type {
  UserEmployee,
  UserEmployeePage
} from '@/features/user/api/user-employee.data.ts'

export interface UseUserOptions {
  userId: string
}

export const userQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: ['employees', userId],
    queryFn: async ({ client, signal }) => {
      // First, try to find the user in any of the cached employee list queries
      const cachedQueries = client.getQueriesData<UserEmployeePage>({
        queryKey: ['employees'],
        type: 'active',
        stale: false
      })

      for (const [, data] of cachedQueries) {
        if (data?.content) {
          const cachedUser = data.content.find((user: UserEmployee) => user.id === userId)
          if (cachedUser) {
            return cachedUser
          }
        }
      }

      // If not found in cache, fetch from API
      return fetchEmployeeById(userId, signal)
    },
    // Keep the data in cache for 5 minutes
    staleTime: 5 * 60 * 1000
  })

/**
 * Hook to fetch a single user by ID.
 * First tries to find the user in the TanStack Query cache from the employee list query.
 * If not found in cache, fetches from the API.
 */
export function useUser({ userId }: UseUserOptions) {
  return useQuery(userQueryOptions(userId))
}
